<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import EditorRenderFrame from './EditorRenderFrame.vue';
import {
  findNode,
  resolveColumnSpans,
  serializeForEditor,
} from '../lib/documentModel';

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  nodesRevision: { type: Number, default: 0 },
  selectedId: { type: String, default: null },
  components: { type: Array, default: () => [] },
  deviceMode: { type: String, default: 'desktop' },
  theme: { type: Object, default: () => ({}) },
  templateId: { type: String, default: '' },
  selectedEventId: { type: [String, Number], default: null },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  active: { type: Boolean, default: false },
  /** Canvas decides when to mount the iframe (pan/visibility / neighbors). */
  frameMounted: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select',
  'deselect',
  'insert',
  'move',
  'remove',
  'duplicate',
  'updateSpans',
  'updateContent',
  'activate',
  'contentHeight',
]);

const { t } = useI18n();

const COMPONENT_MIME = 'component/tag';
const NODE_MIME = 'application/x-portal-node-id';
const LAYOUT_TAGS = new Set([
  'layout-columns',
  'portal-section',
  'portal-tabs',
]);
const EDITABLE_TAGS = new Set([
  'content-heading',
  'content-text',
  'content-button',
]);

const RING = '#3A96DD';
const FEEDBACK_CHROME_ID = 'portal-feedback';
const WIDGET_CHROME_ID = 'portal-assistant';

const frameRef = ref(null);
const overlayRef = ref(null);
const geometry = ref({ nodes: [], tabButtons: [] });
const hoveredId = ref(null);
const contextMenu = ref(null);
const dropTarget = ref(null);
const dragPayload = ref(null);
const resizeState = ref(null);
const localSpans = ref({});
const previewLoading = ref(false);
const editingId = ref(null);
const contentHeight = ref(900);

const editorDocument = computed(() => {
  // Depend on revision so in-place leaf edits refresh preview without array clone
  if (props.nodesRevision < 0) return '';
  return serializeForEditor(props.nodes);
});

const frameWidthClass = computed(() =>
  props.deviceMode === 'mobile' ? 'w-[390px]' : 'w-[1152px]'
);

const artboardHeight = computed(() => {
  const mobilePad = props.deviceMode === 'mobile' ? 32 : 0;
  // Keep a full-page feel even before geometry arrives / for short pages
  return Math.max(contentHeight.value + mobilePad, 900);
});

const onGeometry = payload => {
  if (typeof payload?.contentHeight === 'number' && payload.contentHeight > 0) {
    const next = Math.ceil(payload.contentHeight);
    // Keep the tallest size seen — don't shrink when content is removed
    contentHeight.value = Math.max(contentHeight.value, next);
    const mobilePad = props.deviceMode === 'mobile' ? 32 : 0;
    emit('contentHeight', Math.max(contentHeight.value + mobilePad, 900));
  }
  // Ignore node geometry while resizing — handle position is driven locally
  if (resizeState.value) return;
  if (!props.active) {
    geometry.value = { nodes: [], tabButtons: [] };
    return;
  }
  geometry.value = payload;
};

const componentDefs = computed(() => {
  const map = {};
  props.components.forEach(c => {
    map[c.tag_name] = c;
  });
  return map;
});

const geometryById = computed(() => {
  const map = {};
  (geometry.value.nodes || []).forEach(n => {
    map[n.id] = n;
  });
  return map;
});

const nodeLabel = node => {
  if (!node) return '';
  if (node.kind === 'section') {
    return t('PLANNING.EVENT_PORTALS.EDITOR.SECTION');
  }
  if (node.kind === 'columns') {
    return t('PLANNING.EVENT_PORTALS.EDITOR.COLUMNS');
  }
  if (node.kind === 'tabs') {
    return componentDefs.value['portal-tabs']?.label || 'Tabs';
  }
  if (node.kind === 'tab') {
    return (
      node.props?.name || componentDefs.value['portal-tab']?.label || 'Tab'
    );
  }
  if (node.kind === 'html') return 'HTML';
  if (node.kind === 'wrapper') return node.tagName || 'wrapper';
  if (node.kind === 'block') {
    return componentDefs.value[node.tagName]?.label || node.tagName;
  }
  return node.tagName || node.kind;
};

const labelForId = id => {
  const found = findNode(props.nodes, id);
  return found ? nodeLabel(found.node) : '';
};

/** Map iframe viewport rect → overlay-local coordinates */
const overlayScale = () => {
  if (!overlayRef.value) return 1;
  const box = overlayRef.value.getBoundingClientRect();
  const w = overlayRef.value.offsetWidth || 1;
  return box.width / w || 1;
};

const toOverlayRect = rect => {
  if (!rect || !overlayRef.value || !frameRef.value?.iframeRef) return null;
  const overlayBox = overlayRef.value.getBoundingClientRect();
  const iframeBox = frameRef.value.iframeRef.getBoundingClientRect();
  const scale = overlayScale();
  return {
    top: (iframeBox.top - overlayBox.top) / scale + rect.top,
    left: (iframeBox.left - overlayBox.left) / scale + rect.left,
    width: rect.width,
    height: rect.height,
  };
};

const overlayPoint = e => {
  const box = overlayRef.value?.getBoundingClientRect();
  if (!box) return { x: 0, y: 0 };
  const scale = overlayScale();
  return {
    x: (e.clientX - box.left) / scale,
    y: (e.clientY - box.top) / scale,
  };
};

const highlightIds = computed(() => {
  if (!props.active || resizeState.value) return [];
  const ids = new Set();
  if (props.selectedId) ids.add(props.selectedId);
  if (hoveredId.value) ids.add(hoveredId.value);
  return [...ids];
});

const highlightBoxes = computed(() =>
  highlightIds.value
    .map(id => {
      if (id === FEEDBACK_CHROME_ID || id === WIDGET_CHROME_ID) return null;
      const geo = geometryById.value[id];
      if (!geo) return null;
      const rect = toOverlayRect(geo.rect);
      if (!rect || rect.width < 1) return null;
      return {
        id,
        rect,
        label: labelForId(id),
        selected: id === props.selectedId,
      };
    })
    .filter(Boolean)
);

/** Hit targets sorted smallest-first so nested nodes sit above parents */
const hitTargets = computed(() => {
  if (!props.active) return [];
  return (geometry.value.nodes || [])
    .map(n => {
      const rect = toOverlayRect(n.rect);
      if (!rect || (rect.width < 1 && rect.height < 1)) return null;
      return {
        id: n.id,
        rect,
        area: rect.width * rect.height,
        empty: n.empty,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.area - a.area);
});

const tabHitTargets = computed(() => {
  if (!props.active) return [];
  return (geometry.value.tabButtons || [])
    .map(btn => {
      const rect = toOverlayRect(btn.rect);
      if (!rect) return null;
      return { ...btn, rect };
    })
    .filter(Boolean);
});

const select = id => emit('select', id);
const deselect = () => emit('deselect');

const closeContextMenu = () => {
  contextMenu.value = null;
};

const contextMenuItems = computed(() => [
  {
    key: 'duplicate',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.CONTEXT_DUPLICATE'),
    icon: 'i-lucide-copy',
  },
  {
    key: 'delete',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.CONTEXT_DELETE'),
    icon: 'i-lucide-trash-2',
    danger: true,
  },
]);

const onContextAction = key => {
  const id = contextMenu.value?.id;
  closeContextMenu();
  if (!id) return;
  if (key === 'delete') emit('remove', id);
  if (key === 'duplicate') emit('duplicate', id);
};

const onOverlayClick = () => {
  if (editingId.value) return;
  closeContextMenu();
  deselect();
};

const isEditableNode = id => {
  const found = findNode(props.nodes, id);
  return found?.node?.kind === 'block' && EDITABLE_TAGS.has(found.node.tagName);
};

const beginEdit = id => {
  if (!isEditableNode(id)) return;
  editingId.value = id;
  select(id);
  requestAnimationFrame(() => {
    frameRef.value?.startEdit?.(id);
  });
};

const onNodeClick = (e, id) => {
  e.stopPropagation();
  closeContextMenu();
  if (editingId.value && editingId.value !== id) {
    editingId.value = null;
  }
  if (props.selectedId === id && isEditableNode(id)) {
    beginEdit(id);
    return;
  }
  select(id);
};

const onNodeDblClick = (e, id) => {
  e.stopPropagation();
  e.preventDefault();
  closeContextMenu();
  if (isEditableNode(id)) beginEdit(id);
  else select(id);
};

const onEditCommit = ({ id, content }) => {
  editingId.value = null;
  if (id != null) emit('updateContent', { id, content });
};

const onEditCancel = () => {
  editingId.value = null;
};

const onTabClick = (e, btn) => {
  e.stopPropagation();
  closeContextMenu();
  frameRef.value?.activateTab(btn.tabId);
  select(btn.tabId);
};

const onNodeContextMenu = (e, id) => {
  e.preventDefault();
  e.stopPropagation();
  select(id);
  contextMenu.value = { id, x: e.clientX, y: e.clientY };
};

const onNodeMouseEnter = id => {
  if (dragPayload.value || resizeState.value) return;
  hoveredId.value = id;
};

const onOverlayMouseLeave = () => {
  if (!dragPayload.value) hoveredId.value = null;
};

const collectListSlots = (children, parentId, slots) => {
  const items = (children || []).filter(c => geometryById.value[c.id]);
  if (!items.length) return;

  const first = toOverlayRect(geometryById.value[items[0].id].rect);
  if (first) {
    slots.push({
      parentId,
      index: 0,
      y: first.top,
      left: first.left,
      width: first.width,
    });
  }

  items.forEach((child, i) => {
    const rect = toOverlayRect(geometryById.value[child.id].rect);
    if (!rect) return;
    const next = items[i + 1];
    const nextRect = next
      ? toOverlayRect(geometryById.value[next.id].rect)
      : null;
    const y = nextRect
      ? (rect.top + rect.height + nextRect.top) / 2
      : rect.top + rect.height;
    slots.push({
      parentId,
      index: i + 1,
      y,
      left: rect.left,
      width: rect.width,
    });
  });
};

const walkForSlots = (nodes, parentId, slots) => {
  collectListSlots(nodes, parentId, slots);
  (nodes || []).forEach(node => {
    if (
      node.kind === 'column' ||
      node.kind === 'section' ||
      node.kind === 'tab'
    ) {
      if (!(node.children || []).length) {
        const geo = geometryById.value[node.id];
        if (geo) {
          const rect = toOverlayRect(geo.rect);
          if (rect) {
            slots.push({
              parentId: node.id,
              index: -1,
              empty: true,
              y: rect.top + rect.height / 2,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              top: rect.top,
            });
          }
        }
      } else {
        walkForSlots(node.children, node.id, slots);
      }
    } else if (node.kind === 'columns') {
      (node.children || []).forEach(col => {
        if (!(col.children || []).length) {
          const geo = geometryById.value[col.id];
          if (geo) {
            const rect = toOverlayRect(geo.rect);
            if (rect) {
              slots.push({
                parentId: col.id,
                index: -1,
                empty: true,
                y: rect.top + rect.height / 2,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                top: rect.top,
              });
            }
          }
        } else {
          walkForSlots(col.children, col.id, slots);
        }
      });
    } else if (node.kind === 'tabs') {
      (node.children || []).forEach(tab => {
        if (!(tab.children || []).length) {
          const geo = geometryById.value[tab.id];
          if (geo) {
            const rect = toOverlayRect(geo.rect);
            if (rect) {
              slots.push({
                parentId: tab.id,
                index: -1,
                empty: true,
                y: rect.top + rect.height / 2,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                top: rect.top,
              });
            }
          }
        } else {
          walkForSlots(tab.children, tab.id, slots);
        }
      });
    } else if (node.kind === 'wrapper') {
      walkForSlots(node.children || [], node.id, slots);
    } else if (node.kind === 'block' && node.children?.length) {
      walkForSlots(node.children, node.id, slots);
    }
  });
};

const dropSlots = computed(() => {
  if (!props.active) return [];
  const slots = [];
  if (!props.nodes.length) {
    slots.push({
      parentId: null,
      index: 0,
      empty: true,
      y: 120,
      left: 24,
      width: 400,
      top: 40,
      height: 200,
      pageEmpty: true,
    });
    return slots;
  }
  walkForSlots(props.nodes, null, slots);
  (geometry.value.nodes || []).forEach(n => {
    if (!n.empty) return;
    const found = findNode(props.nodes, n.id);
    if (found?.node?.kind === 'block' || found?.node?.kind === 'html') return;
    if (slots.some(s => s.parentId === n.id && s.index === -1)) return;
    const rect = toOverlayRect(n.rect);
    if (!rect) return;
    slots.push({
      parentId: n.id,
      index: -1,
      empty: true,
      y: rect.top + rect.height / 2,
      left: rect.left,
      width: rect.width,
      top: rect.top,
      height: rect.height,
    });
  });
  return slots;
});

const dropIndicator = computed(() => {
  if (!dropTarget.value) return null;
  const match = dropSlots.value.find(
    s =>
      (s.parentId ?? null) === (dropTarget.value.parentId ?? null) &&
      s.index === dropTarget.value.index
  );
  return match || null;
});

const pickDropSlot = (x, y) => {
  const slots = dropSlots.value;
  if (!slots.length) return { parentId: null, index: props.nodes.length };

  let bestEmpty = null;
  let bestEmptyDist = Infinity;
  slots.forEach(s => {
    if (!s.empty) return;
    const top = s.top ?? s.y - 36;
    const height = s.height ?? 72;
    const left = s.left;
    const width = s.width;
    if (x >= left && x <= left + width && y >= top && y <= top + height) {
      const cy = top + height / 2;
      const dist = Math.abs(y - cy);
      if (dist < bestEmptyDist) {
        bestEmptyDist = dist;
        bestEmpty = s;
      }
    }
  });
  if (bestEmpty) {
    return { parentId: bestEmpty.parentId, index: bestEmpty.index };
  }

  let best = null;
  let bestDist = Infinity;
  slots.forEach(s => {
    if (s.empty) return;
    if (x < s.left - 40 || x > s.left + s.width + 40) return;
    const dist = Math.abs(y - s.y);
    if (dist < bestDist) {
      bestDist = dist;
      best = s;
    }
  });
  if (best && bestDist < 80) {
    return { parentId: best.parentId, index: best.index };
  }
  return { parentId: null, index: props.nodes.length };
};

const hasDragPayload = e => {
  const types = [...(e.dataTransfer?.types || [])];
  return types.includes(COMPONENT_MIME) || types.includes(NODE_MIME);
};

const onOverlayDragOver = e => {
  if (!props.active) return;
  if (!hasDragPayload(e) && !dragPayload.value) return;
  e.preventDefault();
  const { x, y } = overlayPoint(e);
  dropTarget.value = pickDropSlot(x, y);
};

const onOverlayDragLeave = e => {
  if (e.target === overlayRef.value) dropTarget.value = null;
};

const handleDropAt = (tagName, nodeId, target) => {
  dropTarget.value = null;
  dragPayload.value = null;

  if (tagName) {
    if (LAYOUT_TAGS.has(tagName) && target.parentId) {
      emit('insert', {
        tagName,
        target: { parentId: null, index: null },
        preferSiblingOf: target.parentId,
      });
      return;
    }
    emit('insert', { tagName, target });
    return;
  }
  if (nodeId) {
    emit('move', { id: nodeId, target });
  }
};

const onOverlayDrop = e => {
  if (!props.active) return;
  e.preventDefault();
  e.stopPropagation();
  const tagName = e.dataTransfer.getData(COMPONENT_MIME);
  const nodeId = e.dataTransfer.getData(NODE_MIME);
  const { x, y } = overlayPoint(e);
  const target = pickDropSlot(x, y);
  handleDropAt(tagName, nodeId, target);
};

const onNodeDragStart = (e, id) => {
  e.stopPropagation();
  e.dataTransfer.setData(NODE_MIME, id);
  e.dataTransfer.effectAllowed = 'move';
  dragPayload.value = { nodeId: id };
  select(id);
};

const onDocumentDragEnd = () => {
  dropTarget.value = null;
  dragPayload.value = null;
};

const onOverlayDragEnter = e => {
  if (!props.active) return;
  if (hasDragPayload(e)) {
    e.preventDefault();
    dragPayload.value = { external: true };
  }
};

const resizeHandles = computed(() => {
  if (!props.active) return [];
  const chromeId = props.selectedId || hoveredId.value;
  if (!chromeId) return [];
  const found = findNode(props.nodes, chromeId);
  let columnsNode = found?.node;
  if (columnsNode?.kind === 'column') {
    columnsNode = found.parent;
  }
  if (columnsNode?.kind !== 'columns') {
    let walk = found;
    columnsNode = null;
    while (walk?.parent) {
      if (walk.parent.kind === 'columns') {
        columnsNode = walk.parent;
        break;
      }
      walk = findNode(props.nodes, walk.parent.id);
      if (!walk) break;
    }
  }
  if (!columnsNode || columnsNode.kind !== 'columns') return [];

  const cols = columnsNode.children || [];
  const spans = localSpans.value[columnsNode.id] || resolveColumnSpans(cols);
  const dragging = resizeState.value?.columnsId === columnsNode.id;
  const handles = [];
  for (let i = 0; i < cols.length - 1; i += 1) {
    const leftGeo = geometryById.value[cols[i].id];
    const rect = leftGeo ? toOverlayRect(leftGeo.rect) : null;
    const isActiveHandle = dragging && resizeState.value.leftColIdx === i;

    let x;
    let top;
    let height;
    let leftSpan = spans[i];
    let rightSpan = spans[i + 1];

    if (isActiveHandle) {
      x = resizeState.value.handleX;
      top = resizeState.value.top;
      height = resizeState.value.height;
      leftSpan = resizeState.value.currentLeft;
      rightSpan = resizeState.value.currentRight;
    } else if (rect) {
      x = rect.left + rect.width;
      top = rect.top;
      height = rect.height;
    }

    if (x != null) {
      handles.push({
        columnsId: columnsNode.id,
        leftColIdx: i,
        x,
        top,
        height,
        leftSpan,
        rightSpan,
      });
    }
  }
  return handles;
});

let resizeRaf = 0;
let pendingResizeSpans = null;

const flushResizePatch = () => {
  resizeRaf = 0;
  if (!pendingResizeSpans || !resizeState.value) return;
  const { columnsId } = resizeState.value;
  const spans = pendingResizeSpans;
  pendingResizeSpans = null;
  frameRef.value?.patchSpans(columnsId, spans, { skipGeometry: true });
};

const onColumnResizeMove = e => {
  if (!resizeState.value) return;
  const {
    columnsId,
    leftColIdx,
    startX,
    unit,
    originLeftX,
    leftSpan,
    rightSpan,
  } = resizeState.value;
  if (!unit) return;

  const pairTotal = leftSpan + rightSpan;
  const minX = originLeftX + unit;
  const maxX = originLeftX + (pairTotal - 1) * unit;
  const rawHandleX = originLeftX + leftSpan * unit + (e.clientX - startX);
  const handleX = Math.min(maxX, Math.max(minX, rawHandleX));

  const delta = Math.round((e.clientX - startX) / unit);
  const newLeft = Math.min(pairTotal - 1, Math.max(1, leftSpan + delta));
  const newRight = pairTotal - newLeft;

  // Handle tracks the pointer; columns snap on the 12-col grid
  resizeState.value = {
    ...resizeState.value,
    currentLeft: newLeft,
    currentRight: newRight,
    handleX,
  };

  if (
    localSpans.value[columnsId]?.[leftColIdx] === newLeft &&
    localSpans.value[columnsId]?.[leftColIdx + 1] === newRight
  ) {
    return;
  }

  const current = [...(localSpans.value[columnsId] || [])];
  current[leftColIdx] = newLeft;
  current[leftColIdx + 1] = newRight;
  localSpans.value = { ...localSpans.value, [columnsId]: current };
  pendingResizeSpans = current;
  if (!resizeRaf) {
    resizeRaf = requestAnimationFrame(flushResizePatch);
  }
};

const onColumnResizeEnd = () => {
  if (resizeRaf) {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = 0;
  }
  if (pendingResizeSpans && resizeState.value) {
    frameRef.value?.patchSpans(
      resizeState.value.columnsId,
      pendingResizeSpans,
      {
        skipGeometry: true,
      }
    );
    pendingResizeSpans = null;
  }
  if (resizeState.value) {
    const { columnsId } = resizeState.value;
    const spans = localSpans.value[columnsId];
    if (spans) emit('updateSpans', { columnsId, spans });
  }
  resizeState.value = null;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  document.removeEventListener('mousemove', onColumnResizeMove);
  document.removeEventListener('mouseup', onColumnResizeEnd);
  // Refresh chrome geometry after the commit settles
  requestAnimationFrame(() => frameRef.value?.requestGeometry?.());
};

const startColumnResize = (e, handle) => {
  e.preventDefault();
  e.stopPropagation();
  const found = findNode(props.nodes, handle.columnsId);
  if (!found) return;
  const cols = found.node.children || [];
  const spans = resolveColumnSpans(cols);
  localSpans.value = {
    ...localSpans.value,
    [handle.columnsId]: [...spans],
  };

  const leftGeo = geometryById.value[cols[handle.leftColIdx]?.id];
  const rightGeo = geometryById.value[cols[handle.leftColIdx + 1]?.id];
  const leftRect = leftGeo ? toOverlayRect(leftGeo.rect) : null;
  const rightRect = rightGeo ? toOverlayRect(rightGeo.rect) : null;
  const rowWidth =
    leftRect && rightRect
      ? rightRect.left + rightRect.width - leftRect.left
      : (leftRect?.width || 0) * 2;
  if (!rowWidth || !leftRect) return;

  const unit = rowWidth / 12;
  const leftSpan = spans[handle.leftColIdx];
  const rightSpan = spans[handle.leftColIdx + 1];
  const originLeftX = leftRect.left;

  resizeState.value = {
    columnsId: handle.columnsId,
    leftColIdx: handle.leftColIdx,
    startX: e.clientX,
    unit,
    originLeftX,
    top: leftRect.top,
    height: leftRect.height,
    leftSpan,
    rightSpan,
    currentLeft: leftSpan,
    currentRight: rightSpan,
    handleX: originLeftX + leftSpan * unit,
  };
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', onColumnResizeMove);
  document.addEventListener('mouseup', onColumnResizeEnd);
};

watch(
  () => props.nodes,
  () => {
    localSpans.value = {};
  }
);

watch(
  () => props.active,
  async active => {
    if (!active) {
      editingId.value = null;
      hoveredId.value = null;
      dropTarget.value = null;
      closeContextMenu();
      geometry.value = {
        nodes: [],
        tabButtons: [],
      };
      return;
    }
    await nextTick();
    frameRef.value?.requestGeometry?.();
  }
);

const isTypingTarget = el => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return Boolean(el.closest?.('[contenteditable="true"]'));
};

const onKeyDown = e => {
  if (!props.active) return;
  if (e.key !== 'Backspace' && e.key !== 'Delete') return;
  if (!props.selectedId) return;
  if (props.selectedId === FEEDBACK_CHROME_ID) return;
  if (props.selectedId === WIDGET_CHROME_ID) return;
  if (isTypingTarget(e.target)) return;
  e.preventDefault();
  closeContextMenu();
  emit('remove', props.selectedId);
};

const onFramePointerDown = () => {
  emit('activate');
};

onMounted(() => {
  document.addEventListener('dragend', onDocumentDragEnd, true);
  document.addEventListener('keydown', onKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('dragend', onDocumentDragEnd, true);
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('mousemove', onColumnResizeMove);
  document.removeEventListener('mouseup', onColumnResizeEnd);
  document.body.style.cursor = '';
});
</script>

<template>
  <div
    class="relative rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] border overflow-hidden bg-white"
    :class="[
      frameWidthClass,
      active ? 'border-[#3A96DD] ring-2 ring-[#3A96DD]/30' : 'border-n-slate-5',
    ]"
    :style="{ height: `${artboardHeight}px` }"
    @pointerdown="onFramePointerDown"
  >
    <div
      v-if="previewLoading"
      class="absolute top-2 left-2 z-30 px-2 py-1 rounded bg-white/90 text-[10px] text-n-slate-9 border border-n-slate-5"
    >
      <span
        class="i-lucide-loader-2 size-3 animate-spin inline-block align-middle mr-1"
      />
      Updating…
    </div>

    <EditorRenderFrame
      v-if="frameMounted"
      ref="frameRef"
      class="absolute inset-0"
      :template-id="templateId"
      :document="editorDocument"
      :theme="theme"
      :selected-event-id="selectedEventId"
      :portal-editable-attribute-keys="portalEditableAttributeKeys"
      :device-mode="deviceMode"
      annotate
      fit-content
      :geometry-paused="!active"
      :interactive="Boolean(editingId)"
      @geometry="onGeometry"
      @loading="v => (previewLoading = v)"
      @edit-commit="onEditCommit"
      @edit-cancel="onEditCancel"
    />
    <div v-else class="absolute inset-0 bg-white" aria-hidden="true" />

    <div
      v-if="active"
      ref="overlayRef"
      class="absolute inset-0 z-20"
      :class="[
        dragPayload ? 'cursor-copy' : 'cursor-default',
        editingId ? 'pointer-events-none' : '',
      ]"
      @click="onOverlayClick"
      @mouseleave="onOverlayMouseLeave"
      @dragover="onOverlayDragOver"
      @dragenter="onOverlayDragEnter"
      @dragleave="onOverlayDragLeave"
      @drop="onOverlayDrop"
    >
      <div
        v-for="(target, tIdx) in hitTargets"
        :key="`hit-${target.id}`"
        class="absolute cursor-grab active:cursor-grabbing"
        draggable="true"
        :style="{
          top: `${target.rect.top}px`,
          left: `${target.rect.left}px`,
          width: `${target.rect.width}px`,
          height: `${target.rect.height}px`,
          zIndex: tIdx + 1,
        }"
        @click="e => onNodeClick(e, target.id)"
        @dblclick="e => onNodeDblClick(e, target.id)"
        @contextmenu="e => onNodeContextMenu(e, target.id)"
        @mouseenter="onNodeMouseEnter(target.id)"
        @dragstart="e => onNodeDragStart(e, target.id)"
      />

      <div
        v-for="btn in tabHitTargets"
        :key="`tab-${btn.tabId}`"
        class="absolute cursor-pointer"
        :style="{
          top: `${btn.rect.top}px`,
          left: `${btn.rect.left}px`,
          width: `${btn.rect.width}px`,
          height: `${btn.rect.height}px`,
          zIndex: 500,
        }"
        @click="e => onTabClick(e, btn)"
      />

      <div
        v-for="box in highlightBoxes"
        :key="`ring-${box.id}`"
        class="absolute pointer-events-none z-[600] overflow-visible"
        :style="{
          top: `${box.rect.top}px`,
          left: `${box.rect.left}px`,
          width: `${box.rect.width}px`,
          height: `${box.rect.height}px`,
          boxShadow: `inset 0 0 0 3px ${RING}`,
        }"
      >
        <div
          class="absolute left-1 z-20 max-w-[calc(100%-8px)] truncate rounded px-1.5 py-1 text-[10px] font-semibold leading-4 pointer-events-none shadow-sm"
          :style="{
            top: box.rect.top < 24 ? '4px' : '-22px',
            backgroundColor: RING,
            color: '#ffffff',
          }"
        >
          {{ box.label || 'Element' }}
        </div>
      </div>

      <div
        v-if="dropIndicator && !dropIndicator.empty"
        class="absolute h-0.5 rounded-full pointer-events-none z-[700]"
        :style="{
          top: `${dropIndicator.y}px`,
          left: `${dropIndicator.left}px`,
          width: `${dropIndicator.width}px`,
          backgroundColor: RING,
        }"
      />

      <div
        v-if="dropIndicator?.empty"
        class="absolute rounded-lg border-2 border-dashed pointer-events-none z-[700]"
        :style="{
          top: `${dropIndicator.top}px`,
          left: `${dropIndicator.left}px`,
          width: `${dropIndicator.width}px`,
          height: `${dropIndicator.height}px`,
          borderColor: RING,
          backgroundColor: 'rgba(58,150,221,0.08)',
        }"
      />

      <div
        v-if="!nodes.length"
        class="absolute inset-6 flex flex-col items-center justify-center rounded-xl border-2 border-dashed text-center pointer-events-none z-[100]"
        :class="
          dropIndicator?.pageEmpty
            ? 'border-[#3A96DD] bg-[#3A96DD]/10'
            : 'border-n-slate-5 bg-n-slate-1/50'
        "
      >
        <span class="i-lucide-layout-template size-12 mb-3 text-n-slate-6" />
        <p class="text-sm font-medium text-n-slate-11">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.DROP_ELEMENTS') }}
        </p>
        <p class="text-xs mt-1 text-n-slate-9">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.DROP_HINT') }}
        </p>
      </div>

      <div
        v-for="(handle, idx) in resizeHandles"
        :key="`resize-${handle.columnsId}-${idx}`"
        class="absolute z-[800] flex items-center justify-center cursor-col-resize"
        :style="{
          top: `${handle.top}px`,
          left: `${handle.x - 6}px`,
          width: '12px',
          height: `${handle.height}px`,
        }"
        title="Drag to resize"
        @mousedown="e => startColumnResize(e, handle)"
        @click.stop
      >
        <div
          class="w-0.5 h-full rounded-full"
          :class="
            resizeState?.columnsId === handle.columnsId &&
            resizeState?.leftColIdx === handle.leftColIdx
              ? 'bg-[#3A96DD] opacity-100'
              : 'bg-[#3A96DD] opacity-70'
          "
        />
        <span
          v-if="
            resizeState?.columnsId === handle.columnsId &&
            resizeState?.leftColIdx === handle.leftColIdx
          "
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded bg-n-slate-12 text-white text-[10px] font-medium whitespace-nowrap pointer-events-none shadow"
        >
          {{ handle.leftSpan }}/{{ handle.rightSpan }}
        </span>
      </div>
    </div>

    <ContextMenu
      v-if="contextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <div
        class="min-w-44 p-1 rounded-lg bg-white border border-n-slate-5 shadow-lg"
      >
        <button
          v-for="item in contextMenuItems"
          :key="item.key"
          type="button"
          class="flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-left text-xs font-medium transition-colors"
          :class="
            item.danger
              ? 'text-n-ruby-11 hover:bg-n-ruby-2'
              : 'text-n-slate-12 hover:bg-n-slate-2'
          "
          @click="onContextAction(item.key)"
        >
          <span class="size-3.5 shrink-0" :class="[item.icon]" />
          {{ item.label }}
        </button>
      </div>
    </ContextMenu>
  </div>
</template>
